import MyContext from '@/shared/contexts/AppContext';
import React, { useContext, useEffect, useState } from 'react';
import { BiEdit, BiLockAlt, BiMinusCircle, BiPlusCircle, BiShow, BiTrash } from "react-icons/bi";
import { CarregarPermissoes } from '@/modules/system/auth/application/auth.service';
import { GetUsuarioBy } from '@/modules/system/usuario/usuario.service';
import { PermissionEnum } from '@/modules/system/papeis/enum/permission.enum';
import { ReadUsuarioDto } from '@/api.types';
import { RoleEnum } from '@/modules/system/papeis/enum/role.enum';
import { RoleLevelDto } from '@/modules/system/papeis/role-level.dto';
import { toast } from 'react-toastify';
import { ModalAlterarSenha } from '@/modules/system/usuario/ModalAlterarSenha';
import { Collapse } from 'react-bootstrap';

export default function UserProfilePage() {
  const context = useContext(MyContext);
  const [usuario, setUsuario] = useState<ReadUsuarioDto | null>(null);
  const [permissoes, setPermissoes] = useState<RoleLevelDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalAlterarSenhaShow, setModalAlterarSenhaShow] = useState(false);
  const [permissoesAberto, setPermissoesAberto] = useState(false);

  if (!context) throw new Error('UserProfilePage must be used within a MyProvider');

  const { state } = context;

  useEffect(() => {
    const carregarDados = async () => {
      try {
        setLoading(true);

        // Carregar informações do usuário
        if (state.username) {
          const userData = await GetUsuarioBy(state.username);
          setUsuario(userData);
        }

        // Carregar permissões
        const permissoesData = CarregarPermissoes();
        setPermissoes(permissoesData);
      } catch (error) {
        console.error('Erro ao carregar dados do perfil:', error);
        toast.error('Erro ao carregar informações do perfil');
      } finally {
        setLoading(false);
      }
    };

    carregarDados();
  }, [state.username]);

  const hasPermission = (level: PermissionEnum, permission: PermissionEnum): boolean => {
    if (permission === PermissionEnum.NONE) {
      return level === PermissionEnum.NONE;
    }
    return !!(level & permission);
  };

  const getPermissionLevelName = (level: PermissionEnum): string => {
    switch (level) {
      case PermissionEnum.NONE:
        return 'Sem Acesso';
      case PermissionEnum.READ:
        return 'Leitura';
      case PermissionEnum.CREATE:
        return 'Criar';
      case PermissionEnum.UPDATE:
        return 'Atualizar';
      case PermissionEnum.DELETE:
        return 'Remover';
      default:
        return 'Desconhecido';
    }
  };

  const renderPermissionIcon = (permission: PermissionEnum, isActive: boolean) => {
    const size = 20;
    const title = getPermissionLevelName(permission);
    const className = isActive ? '' : 'opacity-25';

    switch (permission) {
      case PermissionEnum.NONE:
        return <BiMinusCircle size={size} className={`text-secondary ${className}`} title={title} />;
      case PermissionEnum.READ:
        return <BiShow size={size} className={`text-primary ${className}`} title={title} />;
      case PermissionEnum.CREATE:
        return <BiPlusCircle size={size} className={`text-success ${className}`} title={title} />;
      case PermissionEnum.UPDATE:
        return <BiEdit size={size} className={`text-warning ${className}`} title={title} />;
      case PermissionEnum.DELETE:
        return <BiTrash size={size} className={`text-danger ${className}`} title={title} />;
      default:
        return null;
    }
  };

  const getRoleName = (role: RoleEnum): string => {
    const roleNames: Partial<Record<RoleEnum, string>> = {
      [RoleEnum.ADMIN_ACTIONS]: 'Ações Administrativas',
      [RoleEnum.GERENCIAR_REGRAS]: 'Gerenciar Regras',
      [RoleEnum.PAPEL]: 'Papéis',
      [RoleEnum.SOLAR]: 'Solar',
      [RoleEnum.USUARIO]: 'Usuários',
      [RoleEnum.ATUACAO]: 'Atuações',
      [RoleEnum.FOLHA]: 'Folha de Frequência',
      [RoleEnum.FOLHA_SEND_NOTIFICATION]: 'Enviar Notificação de Folha',
      [RoleEnum.LOTACAO]: 'Lotações',
      [RoleEnum.MOVIMENTACAO]: 'Movimentações',
      [RoleEnum.PERIODO]: 'Períodos',
      [RoleEnum.SUBSTITUICAO]: 'Substituições',
      [RoleEnum.COLABORADOR]: 'Colaboradores',
      [RoleEnum.DEFENSOR]: 'Defensores',
      [RoleEnum.ESTAGIARIO]: 'Estagiários',
      [RoleEnum.PESSOA]: 'Pessoas',
      [RoleEnum.COMARCA]: 'Comarcas',
      [RoleEnum.CURSO]: 'Cursos',
      [RoleEnum.DEFENSORIA]: 'Defensorias',
      [RoleEnum.DISTRITO]: 'Distritos',
      [RoleEnum.FERIADO]: 'Feriados',
      [RoleEnum.INSTITUICAO_ENSINO]: 'Instituições de Ensino',
      [RoleEnum.NUCLEO]: 'Núcleos',
      [RoleEnum.NUCLEO_MAX_ESTAGIARIOS]: 'Núcleo - Máximo de Estagiários',
      [RoleEnum.PREDIO]: 'Prédios',
      [RoleEnum.VARA]: 'Varas',
    };
    return roleNames[role] || `Role ${role}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="alert alert-warning" role="alert">
        Não foi possível carregar as informações do usuário.
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h3 className="pt-4 mb-0">Perfil do Usuário</h3>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setModalAlterarSenhaShow(true)}
            title="Alterar sua senha"
          >
            <BiLockAlt className="me-1" />
            Alterar senha
          </button>
        </div>
        <div className="col-12">
          <hr />
        </div>
      </div>

      {/* Informações Básicas */}
      <div className="row mb-2">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">{usuario.pessoa?.nome || 'N/A'}</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-3 mb-1">
                  <label className="form-label fw-bold">Nome de Usuário:</label>
                  <p className="form-control-plaintext">{usuario.username || 'N/A'}</p>
                </div>

                {usuario.papel && (
                  <div className="col-md-3 mb-1">
                    <label className="form-label fw-bold">Papel:</label>
                    <p className="form-control-plaintext">{usuario.papel.nome || 'N/A'}</p>
                  </div>
                )}
                <div className="col-md-3 mb-1">
                  <label className="form-label fw-bold">Função:</label>
                  <p className="form-control-plaintext">{usuario.funcao || 'N/A'}</p>
                </div>
                <div className="col-md-3 mb-1">
                  <label className="form-label fw-bold">Recebe Notificações:</label>
                  <p className="form-control-plaintext">
                    <span className={`badge ${usuario.recebenotificacoes ? 'bg-info' : 'bg-secondary'}`}>
                      {usuario.recebenotificacoes ? 'Sim' : 'Não'}
                    </span>
                  </p>
                </div>
                <div className="col-md-3 mb-1">
                  <label className="form-label fw-bold">Email Institucional:</label>
                  <p className="form-control-plaintext">
                    {usuario.pessoa?.emailInstitucional || '-'}
                  </p>
                </div>
                <div className="col-md-3 mb-1">
                  <label className="form-label fw-bold">Email Pessoal:</label>
                  <p className="form-control-plaintext">
                    {usuario.pessoa?.email || '-'}
                  </p>
                </div>

                {usuario.criadoEm && (
                  <div className="col-md-3 mb-1">
                    <label className="form-label fw-bold">Data de Criação:</label>
                    <p className="form-control-plaintext">
                      {new Date(usuario.criadoEm).toLocaleDateString('pt-BR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                        second: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Permissões */}
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div
              className="card-header d-flex justify-content-between align-items-center"
              onClick={() => setPermissoesAberto(!permissoesAberto)}
              style={{ cursor: 'pointer' }}
              aria-expanded={permissoesAberto}
              aria-controls="collapse-permissoes"
            >
              <h5 className="mb-0">Permissões</h5>
              <span className="small">{permissoesAberto ? '▼' : '▶'}</span>
            </div>
            <Collapse in={permissoesAberto}>
              <div className="card-body" id="collapse-permissoes">
                {permissoes.length === 0 ? (
                  <p className="text-muted">Nenhuma permissão encontrada.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead>
                        <tr>
                          <th>Módulo</th>
                          <th className="text-center">Nível</th>
                          <th>Módulo</th>
                          <th className="text-center">Nível</th>
                          <th>Módulo</th>
                          <th className="text-center">Nível</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const sortedPermissoes = permissoes.sort((a, b) => Number(a.role) - Number(b.role));
                          const permissionLevels = [
                            PermissionEnum.NONE,
                            PermissionEnum.READ,
                            PermissionEnum.CREATE,
                            PermissionEnum.UPDATE,
                            PermissionEnum.DELETE,
                          ];
                          const rows: JSX.Element[] = [];

                          // Agrupar em grupos de 3
                          for (let i = 0; i < sortedPermissoes.length; i += 3) {
                            const grupo = sortedPermissoes.slice(i, i + 3);

                            rows.push(
                              <tr key={i}>
                                {grupo.map((permissao, idx) => (
                                  <React.Fragment key={idx}>
                                    <td>{getRoleName(permissao.role)}</td>
                                    <td className="text-center">
                                      <div className="d-flex justify-content-center gap-1">
                                        {permissionLevels.map((level) => {
                                          const isReached = hasPermission(permissao.level, level);
                                          return (
                                            <span key={level} title={getPermissionLevelName(level)}>
                                              {renderPermissionIcon(level, isReached)}
                                            </span>
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </React.Fragment>
                                ))}
                                {/* Preencher células vazias se o grupo tiver menos de 3 itens */}
                                {grupo.length < 3 && Array.from({ length: (3 - grupo.length) * 2 }).map((_, idx) => (
                                  <td key={`empty-${idx}`}></td>
                                ))}
                              </tr>
                            );
                          }

                          return rows;
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Collapse>
          </div>
        </div>
      </div>

      <ModalAlterarSenha
        show={modalAlterarSenhaShow}
        onClose={() => setModalAlterarSenhaShow(false)}
      />
    </div>
  );
}

